import React from "react";
import styled from "styled-components";
import useNewLookNextOrder, {
  NewLookNextOrderProps,
} from "features/bots/NewLook/useNewLookNextOrder";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

/**
 * New look: a live next order on one line — the signed distance, then the
 * muted price. Neutral like the bot card's trade range: colour stays for PnL.
 */
const NewLookNextOrder = (props: NewLookNextOrderProps) => {
  const { distanceText, price } = useNewLookNextOrder(props);

  if (!props.nextOrder) return <Price>N/A</Price>;

  return (
    <Wrapper>
      <Distance>{distanceText}</Distance>
      <Price>{price}</Price>
    </Wrapper>
  );
};

export default React.memo(NewLookNextOrder);

/**
 * New look (table cell): the live distance as a figure over the level's
 * price, right-aligned like the table's other figures.
 */
export const NewLookNextOrderCell = React.memo(
  (props: NewLookNextOrderProps) => {
    const { distanceText, price } = useNewLookNextOrder(props);
    return (
      <Cell>
        <CellFigure>{distanceText}</CellFigure>
        <CellMeta>{price}</CellMeta>
      </Cell>
    );
  }
);

const Wrapper = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;
const Distance = styled.span`
  color: ${(p) => p.theme.colors.primaryText};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const Price = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(p) => p.theme.colors.secondaryText};
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const Cell = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  white-space: nowrap;
`;
const CellFigure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const CellMeta = styled.span`
  display: inline-flex;
  align-items: center;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
